import React from "react";

import * as UpcomingEventUtils from "@app/analytics/Calendar/UpcomingEvents/utils";
import type { UserEventGroup } from "@app/analytics/types/eventGroupTypes";
import AccessList, {
  AccessLevelChange,
} from "@app/common/access/ShareReport/AccessList";
import type { UseEditSharing } from "@app/common/access/ShareReport/hooks/useEditSharing";
import ShareReportAddEntities from "@app/common/access/ShareReport/ShareReportAddEntities";
import type { EventGroupACLResponse } from "@app/common/types";
import type { ParticipantAccessLevel } from "@app/common/types";

import { Link } from "@rui/Link";
import { Bold } from "@rui/styles";

import { ContentContainer, WarningMessage } from "./styles";

const EMPTY_ACCESS: EventGroupACLResponse["entityAccess"] = [];

interface ShareWarningProps {
  externalParticipantsRole: ParticipantAccessLevel;
  internalParticipantsRole: ParticipantAccessLevel;
  onGoToTab: (tab: "sharing" | "distribution") => void;
}

type ShareReportProps = UseEditSharing &
  ShareWarningProps & {
    accessList?: EventGroupACLResponse;
    event: UserEventGroup;
    open: boolean;
    onModifyAccess: (update: AccessLevelChange) => void;
    onRevokeAccess: (entityId: string) => void;
  };

const SharingWarning = ({
  internalParticipantsRole,
  externalParticipantsRole,
  onGoToTab,
}: ShareWarningProps) => {
  const description = UpcomingEventUtils.getAccessDescription({
    internalParticipantsRole,
    externalParticipantsRole,
  });

  if (!description) return null;

  return (
    <WarningMessage>
      Based on{" "}
      <Link onClick={() => onGoToTab("distribution")}>
        Access & Distribution
      </Link>{" "}
      preferences, <Bold>{description}</Bold> to the meeting report. Individuals
      added below will still be granted access.
    </WarningMessage>
  );
};

const ShareReportTab = ({
  accessList,
  externalParticipantsRole,
  internalParticipantsRole,
  onGoToTab,
  onModifyAccess,
  onRevokeAccess,
  ...editSharing
}: ShareReportProps) => {
  const {
    bulkAccess,
    isAddingEntities,
    onChangeBulkAccess,
    onChangeSelectedEntities,
    onAddEntity,
    selectedEntities,
  } = editSharing;

  return (
    <ContentContainer spacing={1.5} padding={2}>
      <SharingWarning
        externalParticipantsRole={externalParticipantsRole}
        internalParticipantsRole={internalParticipantsRole}
        onGoToTab={onGoToTab}
      />
      <ShareReportAddEntities
        accessList={accessList}
        bulkAccess={bulkAccess}
        isAddingEntities={isAddingEntities}
        onChangeBulkAccess={onChangeBulkAccess}
        onInputChange={onChangeSelectedEntities}
        onSelectSuggestion={onAddEntity}
        selectedEntities={selectedEntities}
      />
      <AccessList
        entities={accessList?.entityAccess ?? EMPTY_ACCESS}
        heading={{
          title: "People you will share with",
          tooltip:
            "These are additional people you would like to share the report with. Meeting participants are not shown here.",
        }}
        onChange={onModifyAccess}
        onRemove={onRevokeAccess}
        maxHeight="214px"
      />
    </ContentContainer>
  );
};

export default ShareReportTab;
